/**
 * The few line icons of the redesign, inline: they follow the text colour (`currentColor`) and are hidden from screen
 * readers, the button or text next to them names what they mean. Drawn on a 24×24 grid like the mockups.
 */
interface props {
    size?: number;
    strokeWidth?: number;
    children: React.ReactNode;
}

const Icon = ({size = 18, strokeWidth = 1.8, children}: props): React.JSX.Element => (
    <svg
        aria-hidden="true"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {children}
    </svg>
);

export const PrinterIcon = (): React.JSX.Element => (
    <Icon>
        <path d="M6 9V3h12v6" />
        <rect x="3" y="9" width="18" height="8" rx="2" />
        <path d="M7 14h10v7H7z" />
    </Icon>
);

/** a barcode in a viewfinder: the terminal waits for a scan */
export const ScanIcon = (): React.JSX.Element => (
    <Icon size={96} strokeWidth={1.2}>
        <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
        <path d="M7 8v8M10 8v8M13 8v8M17 8v8" />
    </Icon>
);

export const MoonIcon = (): React.JSX.Element => (
    <Icon>
        <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
    </Icon>
);

export const SunIcon = (): React.JSX.Element => (
    <Icon>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
);

/** after the label of a button that opens a menu */
export const ChevronIcon = (): React.JSX.Element => (
    <Icon size={14} strokeWidth={2}>
        <path d="m6 9 6 6 6-6" />
    </Icon>
);

export const CheckIcon = (): React.JSX.Element => (
    <Icon size={20} strokeWidth={2.4}>
        <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Icon>
);

export const CloseIcon = (): React.JSX.Element => (
    <Icon size={22} strokeWidth={2}>
        <path d="M6 6l12 12M18 6 6 18" />
    </Icon>
);

export const ArrowUpIcon = (): React.JSX.Element => (
    <Icon size={12} strokeWidth={2.4}>
        <path d="M12 20V4M6 10l6-6 6 6" />
    </Icon>
);

export const ArrowDownIcon = (): React.JSX.Element => (
    <Icon size={12} strokeWidth={2.4}>
        <path d="M12 4v16M6 14l6 6 6-6" />
    </Icon>
);

/** in front of an error message, `large` above the message of a page that could not be shown */
export const AlertIcon = ({large = false}: {large?: boolean}): React.JSX.Element => (
    <Icon size={large ? 64 : 22} strokeWidth={large ? 1.5 : 2}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 7v6M12 16.5v.5" />
    </Icon>
);

/** after the label of a button that opens another site */
export const ExternalLinkIcon = (): React.JSX.Element => (
    <Icon>
        <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </Icon>
);

/** the moving truck that drives across the status band on every change of the status */
export const TruckIcon = (): React.JSX.Element => (
    <Icon size={48} strokeWidth={1.6}>
        <path d="M2 16V7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9" />
        <path d="M14 9h4l3 4v3h-2M14 16h1M9 16H5" />
        <circle cx="7" cy="17" r="2" />
        <circle cx="17" cy="17" r="2" />
    </Icon>
);
